import { useState, useCallback, useMemo, useEffect } from 'react';
import { format, parse, isValid } from 'date-fns';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CaretDownIcon, XIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import { cn } from '@/lib/utils';

/** Convert a Date to YYYYMMDD string */
function dateToYYYYMMDD(date: Date): string {
  return format(date, 'yyyyMMdd');
}

/** Convert a YYYYMMDD string to a Date */
function yyyymmddToDate(str: string): Date | undefined {
  if (!/^\d{8}$/.test(str)) return undefined;
  const parsed = parse(str, 'yyyyMMdd', new Date());
  return isValid(parsed) ? parsed : undefined;
}

/** Parse `gte:YYYYMMDD[HHmm]` / `lte:YYYYMMDD[HHmm]` / `eq:YYYYMMDD` entries.
 *  `eq:X` is treated as from=to=X. 12-digit values carry HH:mm time. */
function parseFilterValues(values: string[]): {
  from?: Date;
  to?: Date;
  fromTime?: string;
  toTime?: string;
} {
  let gte: string | undefined;
  let lte: string | undefined;
  let eq: string | undefined;

  for (const entry of values) {
    const colonIdx = entry.indexOf(':');
    if (colonIdx === -1) continue;
    const op = entry.slice(0, colonIdx);
    const val = entry.slice(colonIdx + 1);
    if (op === 'gte') gte = val;
    else if (op === 'lte') lte = val;
    else if (op === 'eq') eq = val;
  }

  const extract = (val: string | undefined): { date?: Date; time?: string } => {
    if (!val) return {};
    if (/^\d{12}$/.test(val)) {
      const date = yyyymmddToDate(val.slice(0, 8));
      const hh = val.slice(8, 10);
      const mm = val.slice(10, 12);
      return { date, time: `${hh}:${mm}` };
    }
    return { date: yyyymmddToDate(val) };
  };

  if (eq) {
    const { date } = extract(eq);
    return { from: date, to: date };
  }
  const fromParsed = extract(gte);
  const toParsed = extract(lte);
  return {
    from: fromParsed.date,
    to: toParsed.date,
    fromTime: fromParsed.time,
    toTime: toParsed.time,
  };
}

/** Human-readable summary for the button label */
function buildSummary(
  from?: Date,
  to?: Date,
  fromTime?: string,
  toTime?: string,
): string | null {
  if (!from || !to) return null;
  const fromStr = format(from, 'MM/dd/yy');
  const toStr = format(to, 'MM/dd/yy');
  const hasTime = fromTime || toTime;
  if (fromStr === toStr && !hasTime) return fromStr;
  if (fromStr === toStr && hasTime)
    return `${fromStr} ${fromTime ?? '00:00'}–${toTime ?? '23:59'}`;
  return `${fromStr} – ${toStr}`;
}

/** Build the filter string for a date, optionally with time */
function buildFilterVal(date: Date, time?: string): string {
  const base = dateToYYYYMMDD(date);
  if (!time) return base;
  return `${base}${time.replace(':', '')}`;
}

interface DateFilterProps {
  label: string;
  value: string[];
  onChange: (values: string[] | undefined) => void;
  showTime?: boolean;
  /** Extra classes for the trigger button (e.g. to match a filter row's height). */
  className?: string;
  /**
   * When true, the active trigger shows only the date summary (no `label:`
   * prefix). Use when an external `<Label>` already names the field.
   */
  summaryOnly?: boolean;
  /**
   * When true, opening the filter with no value pre-fills today's date on both
   * the From and To ends (and commits it) as a sensible default.
   */
  defaultToTodayOnOpen?: boolean;
}

export function DateFilter({
  label,
  value,
  onChange,
  showTime,
  className,
  summaryOnly,
  defaultToTodayOnOpen,
}: DateFilterProps) {
  const [open, setOpen] = useState(false);

  // Serialize the committed `value` so we can detect real content changes.
  // The parent typically passes `filters[col.apiField!] ?? []`, which
  // produces a fresh empty array on every render when the key is absent;
  // deriving parsed off a stable string key prevents `parseFilterValues`
  // from minting new Date instances each render and firing any effect that
  // depends on them spuriously. We reconstruct the values array from the
  // key so `value` itself isn't a useMemo dep.
  const valueKey = value.join(',');
  const parsed = useMemo(
    () => parseFilterValues(valueKey === '' ? [] : valueKey.split(',')),
    [valueKey],
  );

  // Local (uncommitted) range. Only pushed to the URL when BOTH ends are set.
  const [localFrom, setLocalFrom] = useState<Date | undefined>(parsed.from);
  const [localTo, setLocalTo] = useState<Date | undefined>(parsed.to);
  const [localFromTime, setLocalFromTime] = useState<string>(
    parsed.fromTime ?? '',
  );
  const [localToTime, setLocalToTime] = useState<string>(parsed.toTime ?? '');

  // Resync local to the committed value only when the committed value
  // actually changes (not on every parent re-render). In-progress draft
  // edits live in localFrom/localTo and must not be clobbered by unrelated
  // URL updates.
  useEffect(() => {
    setLocalFrom(parsed.from);
    setLocalTo(parsed.to);
    setLocalFromTime(parsed.fromTime ?? '');
    setLocalToTime(parsed.toTime ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const summary = useMemo(
    () => buildSummary(parsed.from, parsed.to, parsed.fromTime, parsed.toTime),
    [parsed.from, parsed.to, parsed.fromTime, parsed.toTime],
  );
  const isActive = summary !== null;

  const commit = useCallback(
    (from?: Date, to?: Date, fromTime?: string, toTime?: string) => {
      if (!from || !to) {
        // Incomplete range — don't filter yet. If there was a prior filter,
        // clear it so stale results don't linger.
        if (isActive) onChange(undefined);
        return;
      }
      const ft = fromTime || undefined;
      const tt = toTime || undefined;

      let earlier = from;
      let later = to;
      let earlierTime = ft;
      let laterTime = tt;
      if (
        earlier.getTime() > later.getTime() ||
        (earlier.getTime() === later.getTime() &&
          earlierTime &&
          laterTime &&
          earlierTime > laterTime)
      ) {
        [earlier, later] = [later, earlier];
        [earlierTime, laterTime] = [laterTime, earlierTime];
      }
      // When from === to (same day) and no time specified, emit a single `eq` entry.
      const sameDay = dateToYYYYMMDD(earlier) === dateToYYYYMMDD(later);
      if (sameDay && !earlierTime && !laterTime) {
        onChange([`eq:${dateToYYYYMMDD(earlier)}`]);
      } else {
        onChange([
          `gte:${buildFilterVal(earlier, earlierTime)}`,
          `lte:${buildFilterVal(later, laterTime)}`,
        ]);
      }
    },
    [isActive, onChange],
  );

  const handleFromChange = useCallback(
    (date: Date | undefined) => {
      setLocalFrom(date);
      commit(date, localTo, localFromTime, localToTime);
    },
    [commit, localTo, localFromTime, localToTime],
  );

  const handleToChange = useCallback(
    (date: Date | undefined) => {
      setLocalTo(date);
      commit(localFrom, date, localFromTime, localToTime);
    },
    [commit, localFrom, localFromTime, localToTime],
  );

  const handleFromTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const t = e.target.value;
      setLocalFromTime(t);
      commit(localFrom, localTo, t, localToTime);
    },
    [commit, localFrom, localTo, localToTime],
  );

  const handleToTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const t = e.target.value;
      setLocalToTime(t);
      commit(localFrom, localTo, localFromTime, t);
    },
    [commit, localFrom, localTo, localFromTime],
  );

  const handleClear = useCallback(() => {
    setLocalFrom(undefined);
    setLocalTo(undefined);
    setLocalFromTime('');
    setLocalToTime('');
    onChange(undefined);
    setOpen(false);
  }, [onChange]);

  // When opted in, opening an empty filter pre-fills today on both ends and
  // commits it, so clicking the filter defaults to "today" (the user can then
  // widen the range).
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next && defaultToTodayOnOpen && !localFrom && !localTo) {
        const today = new Date();
        setLocalFrom(today);
        setLocalTo(today);
        commit(today, today, localFromTime, localToTime);
      }
    },
    [
      defaultToTodayOnOpen,
      localFrom,
      localTo,
      commit,
      localFromTime,
      localToTime,
    ],
  );

  return (
    <div>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-7 justify-between font-normal max-w-[280px]',
              isActive ? 'border-foreground' : 'text-muted-foreground',
              className,
            )}
          >
            <span className="truncate">
              {isActive
                ? summaryOnly
                  ? summary
                  : `${label}: ${summary}`
                : label}
            </span>
            <CaretDownIcon className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-3" align="start">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{label}</p>
              {(localFrom || localTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={handleClear}
                >
                  <XIcon className="h-3 w-3 mr-1" />
                  <T context="ship-and-debit date filter clear button">Clear</T>
                </Button>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                <T context="ship-debit">From</T>
              </p>
              <div className="flex items-center gap-2">
                <DatePicker value={localFrom} onChange={handleFromChange} />
                {showTime && (
                  <input
                    type="time"
                    value={localFromTime}
                    onChange={handleFromTimeChange}
                    className="h-8 w-[90px] rounded-md border border-input bg-background px-2 text-sm"
                  />
                )}
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                <T context="ship-debit">To</T>
              </p>
              <div className="flex items-center gap-2">
                <DatePicker value={localTo} onChange={handleToChange} />
                {showTime && (
                  <input
                    type="time"
                    value={localToTime}
                    onChange={handleToTimeChange}
                    className="h-8 w-[90px] rounded-md border border-input bg-background px-2 text-sm"
                  />
                )}
              </div>
            </div>

            {!isActive && (localFrom || localTo) && (
              <p className="text-xs text-muted-foreground">
                <T context="ship-debit">
                  Pick both From and To to apply the filter. Set them to the
                  same day to filter that specific date.
                </T>
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
